// =============================================================================
// FxBreakdownCard, reusable "As of FX rates …" disclosure + breakdown table.
// =============================================================================
//
// Used on any aggregate page (Net Worth, Dashboard, Projections, FI Tracker,
// Goals' combined view). Renders the disclosure line + an expandable
// per-currency breakdown table.
//
// Props:
//   • title             , section header (e.g. "Net worth breakdown")
//   • base              , user's base currency code (e.g. "INR")
//   • breakdown         , array from the backend `/api/*/breakdown` endpoints
//   • headline          , { as_of_date, source, stale_days, currencies_in_use }
//   • valueColumns      , array of { key, label, basesKey } describing which
//                          columns to render. Defaults to net_worth-style
//                          single-value (`{ key: 'net', label: 'Net', basesKey: 'base_net' }`).
//   • warnings          , array of strings (rate-unavailable messages)
//   • testId            , root data-testid
//   • compact           , when true, omits the title/header (used inline)
//
// Self-hides when only 1 currency is in use AND no warnings, no noise.
// =============================================================================
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Globe, ChevronDown } from 'lucide-react';
import { formatMoney, fxAsOfLine } from '@/lib/currency';

export default function FxBreakdownCard({
  title = null,
  base,
  breakdown,
  headline,
  valueColumns,
  warnings = [],
  testId = 'fx-breakdown',
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const currencies = headline?.currencies_in_use || (breakdown || []).map(r => r.currency);
  const isMultiCurrency = currencies && currencies.length > 1;
  const hasWarnings = warnings && warnings.length > 0;

  // Self-hide when single currency and no warnings, nothing to disclose.
  if (!isMultiCurrency && !hasWarnings) return null;

  // Default to net_worth-style columns when none provided.
  const cols = valueColumns || [
    { key: 'net', label: 'Net', nativeKey: 'native_net', baseKey: 'base_net' },
  ];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="rounded-2xl border-border/60 bg-card/50" data-testid={testId}>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2.5 text-xs text-muted-foreground" data-testid={`${testId}-as-of`}>
              <Globe className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                {title && !compact && <div className="font-medium text-foreground mb-0.5">{title}</div>}
                <div>{fxAsOfLine(headline)}</div>
                {isMultiCurrency && (
                  <div className="mt-0.5 text-[11px] opacity-75">
                    Held in {currencies.join(', ')}, converted to {base} for the headline.
                  </div>
                )}
              </div>
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 rounded-lg text-xs" data-testid={`${testId}-toggle`}>
                {open ? 'Hide' : 'Show'} breakdown
                <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent className="mt-3">
            <div className="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
              <table className="w-full text-xs" data-testid={`${testId}-table`}>
                <thead>
                  <tr className="text-muted-foreground border-b border-border/40">
                    <th className="text-left font-medium py-2 pr-3">Currency</th>
                    {cols.map(c => (
                      <th key={c.key} className="text-right font-medium py-2 px-3">Native {c.label.toLowerCase()}</th>
                    ))}
                    <th className="text-right font-medium py-2 px-3">Rate</th>
                    <th className="text-right font-medium py-2 pl-3 hidden sm:table-cell">As of</th>
                    {cols.map(c => (
                      <th key={`b-${c.key}`} className="text-right font-medium py-2 pl-3">{c.label} in {base}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((row) => (
                    <tr key={row.currency} className="border-b border-border/20 last:border-b-0" data-testid={`${testId}-row-${row.currency}`}>
                      <td className="py-2 pr-3 font-medium">{row.currency}</td>
                      {cols.map(c => (
                        <td key={`n-${c.key}`} className="py-2 px-3 text-right tabular-nums">
                          {formatMoney(row[c.nativeKey], row.currency)}
                        </td>
                      ))}
                      <td className="py-2 px-3 text-right tabular-nums opacity-80">
                        {row.rate ? (row.currency === base ? ' ' : Number(row.rate).toFixed(4)) : ' '}
                      </td>
                      <td className="py-2 pl-3 text-right opacity-70 hidden sm:table-cell">
                        {row.as_of_date ? new Date(`${row.as_of_date}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ' '}
                      </td>
                      {cols.map(c => (
                        <td key={`b-${c.key}`} className="py-2 pl-3 text-right tabular-nums font-medium">
                          {row[c.baseKey] !== null && row[c.baseKey] !== undefined
                            ? formatMoney(row[c.baseKey], base)
                            : <span className="text-destructive text-[10px]">rate unavailable</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasWarnings && (
              <div className="mt-3 rounded-lg bg-destructive/10 border border-destructive/30 px-3 py-2 text-[11px] text-destructive" data-testid={`${testId}-warnings`}>
                {warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
}
